import { Alert, Unstable_Grid2 as Grid } from '@mui/material';
import { useField, useForm } from 'react-final-form';

import { InfoIcon, Typography } from '@occc/ui-design-system';
import { OnChange, TextField, ToggleButtonGroupField } from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { BannerConfigurationFragment, Category_Restrictions } from '@occc/graphql';
import config from '@occc/config';

import { ExternalBannersSelectField } from './ExternalBannersSelect';

import { testIdValues, getInputProps } from '../utils';

const categoryRestrictionItems = [
  { title: 'None', value: 'None' },
  { title: 'Age', value: Category_Restrictions.Age },
  { title: 'EAIV', value: Category_Restrictions.Eaiv },
];

const loadingBehaviorItems = [
  { title: 'Preload Offers', value: false, key: 'preload' },
  { title: 'Load On Entry', value: true, key: 'lazy' },
];

export const FormContent = () => {
  const form = useForm();
  const {
    input: { value: banners },
  } = useField<BannerConfigurationFragment[]>('banners', { subscription: { value: true } });
  const hasExternalSource = (banners?.length ?? 0) > 0;

  return (
    <Grid container spacing={3}>
      {config.features.externalBannersEnabled && (
        <OnChange name='banners'>
          {(value: BannerConfigurationFragment[] | undefined) => {
            const hasBanners = (value?.length ?? 0) > 0;
            const isBannersDirty = Boolean(form.getFieldState('banners')?.dirty);

            if (!hasBanners) {
              if (isBannersDirty) {
                form.change('shouldLazyLoadBanners', undefined);
              }
              return;
            }

            if (form.getState().values.shouldLazyLoadBanners === undefined) {
              form.change('shouldLazyLoadBanners', false);
            }
          }}
        </OnChange>
      )}
      <Grid xs={12} md={config.features.externalBannersEnabled ? 4 : 6}>
        <TextField
          name='name'
          label='Name'
          margin='normal'
          inputProps={getInputProps(testIdValues.name)}
        />
      </Grid>
      <Grid xs={12} md={config.features.externalBannersEnabled ? 4 : 6}>
        <TextField
          type='number'
          name='rank'
          label='Rank'
          margin='normal'
          inputProps={getInputProps(testIdValues.rank)}
          fieldProps={{
            parse: parseNumber,
          }}
        />
      </Grid>
      {config.features.externalBannersEnabled && (
        <Grid xs={12} md={4}>
          <ExternalBannersSelectField name='banners' />
        </Grid>
      )}

      <Grid xs={12} md={6}>
        <ToggleButtonGroupField
          name='categoryRestrictions'
          buttons={categoryRestrictionItems}
          label='Restriction'
          fullWidth
        />
      </Grid>
      {config.features.externalBannersEnabled && hasExternalSource && (
        <Grid xs={12} md={6}>
          <ToggleButtonGroupField
            name='shouldLazyLoadBanners'
            buttons={loadingBehaviorItems}
            label='Loading behavior'
            fullWidth
          />
          <Alert icon={<InfoIcon />} severity='info' sx={{ mt: 2, alignItems: 'center' }}>
            <Typography variant='body2'>
              Preload fetches contents when the app loads and caches them. Load on entry fetches
              fresh contents each time the category is opened.
            </Typography>
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};
